import React, { useState } from "react";
import api, { formatApiError } from "@/lib/api";
import { X, Check, Copy, UserPlus, AlertCircle } from "lucide-react";

const NZ_REGIONS = [
  "Northland", "Auckland", "Waikato", "Bay of Plenty", "Gisborne", "Hawke's Bay",
  "Taranaki", "Manawatū-Whanganui", "Wellington", "Tasman", "Nelson", "Marlborough",
  "West Coast", "Canterbury", "Otago", "Southland",
];

export default function InviteGrowerModal({ onClose, onInvited }) {
  const [form, setForm] = useState({ name: "", email: "", farm_name: "", region: "" });
  const [busy, setBusy] = useState(false);
  const [err, setErr] = useState("");
  const [result, setResult] = useState(null);
  const [copied, setCopied] = useState(false);

  const set = (k, v) => setForm((f) => ({ ...f, [k]: v }));

  const submit = async (e) => {
    e.preventDefault();
    setErr("");
    if (form.name.trim().length < 2 || !form.email.includes("@")) {
      setErr("Please give a name and a valid email.");
      return;
    }
    setBusy(true);
    try {
      const { data } = await api.post("/admin/growers", form);
      setResult(data);
      onInvited?.(data.user);
    } catch (e) {
      setErr(formatApiError(e.response?.data?.detail));
    } finally {
      setBusy(false);
    }
  };

  const copy = () => {
    navigator.clipboard.writeText(result.temp_password);
    setCopied(true);
    setTimeout(() => setCopied(false), 1500);
  };

  return (
    <div className="fixed inset-0 z-50 bg-[var(--bloom-ink)]/60 backdrop-blur-sm flex items-center justify-center p-4" onClick={onClose} data-testid="invite-modal">
      <div className="bg-[var(--bloom-bg)] w-full max-w-lg" onClick={(e) => e.stopPropagation()}>
        <div className="flex items-center justify-between p-5 border-b border-[var(--bloom-border)]">
          <div className="flex items-center gap-2">
            <UserPlus size={16} strokeWidth={1.5} />
            <h2 className="font-serif text-2xl">Invite a Grower</h2>
          </div>
          <button onClick={onClose} className="p-1.5 hover:bg-[var(--bloom-surface)]" data-testid="invite-modal-close"><X size={16} /></button>
        </div>

        {!result ? (
          <form onSubmit={submit} className="p-8" data-testid="invite-form">
            <p className="font-serif italic text-sm text-[var(--bloom-ink-soft)] mb-6">
              Create an account for a grower so they can sign in and start reading the Prospectus.
              They&rsquo;ll get a temporary password you can share with them.
            </p>

            <div className="space-y-4">
              <Field label="Full name *">
                <input value={form.name} onChange={(e) => set("name", e.target.value)} className="bloom-input" autoFocus data-testid="invite-name" />
              </Field>
              <Field label="Email *">
                <input type="email" value={form.email} onChange={(e) => set("email", e.target.value)} className="bloom-input" data-testid="invite-email" />
              </Field>
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                <Field label="Farm name">
                  <input value={form.farm_name} onChange={(e) => set("farm_name", e.target.value)} className="bloom-input" data-testid="invite-farm" />
                </Field>
                <Field label="Region">
                  <select value={form.region} onChange={(e) => set("region", e.target.value)} className="bloom-input" data-testid="invite-region">
                    <option value=""> </option>
                    {NZ_REGIONS.map((r) => <option key={r} value={r}>{r}</option>)}
                  </select>
                </Field>
              </div>
            </div>

            {err && <div className="mt-4 flex items-center gap-2 text-sm text-[var(--bloom-accent)] font-serif italic" data-testid="invite-error"><AlertCircle size={14} /> {err}</div>}

            <div className="mt-8 flex justify-end gap-3">
              <button type="button" onClick={onClose} className="bloom-btn bloom-btn-ghost">Cancel</button>
              <button type="submit" disabled={busy} className="bloom-btn bloom-btn-primary" data-testid="invite-submit">
                {busy ? "Creating…" : "Create Account"}
              </button>
            </div>
          </form>
        ) : (
          <div className="p-8" data-testid="invite-success">
            <Check className="mx-auto mb-3 text-[var(--bloom-ink)]" size={36} strokeWidth={1.5} />
            <h3 className="font-serif text-2xl text-center mb-1">{result.user.name} added.</h3>
            <p className="text-center font-serif italic text-[var(--bloom-ink-soft)] mb-6">{result.user.email}</p>

            <div className="border border-[var(--bloom-border)] bg-[var(--bloom-surface)] p-5">
              <div className="bloom-label mb-2">Temporary password</div>
              <div className="flex items-center justify-between gap-3">
                <code className="font-mono text-base" data-testid="invite-temp-password">{result.temp_password}</code>
                <button onClick={copy} className="bloom-btn bloom-btn-outline text-xs" data-testid="invite-copy-password">
                  <Copy size={12} /> {copied ? "Copied" : "Copy"}
                </button>
              </div>
              <p className="text-xs italic text-[var(--bloom-ink-muted)] mt-3 font-serif">
                Share this with the grower so they can sign in at <strong>/login</strong>. They&rsquo;ll be asked to change it on first sign-in.
              </p>
            </div>

            <div className="mt-6 text-center">
              <button onClick={onClose} className="bloom-btn bloom-btn-primary" data-testid="invite-done">Done</button>
            </div>
          </div>
        )}
      </div>
    </div>
  );
}

function Field({ label, children }) {
  return (
    <div>
      <div className="bloom-label mb-2">{label}</div>
      {children}
    </div>
  );
}
